import React, { useState, useRef, useEffect } from 'react'
import { apiUploadFile, getErrorMessage } from '../../utils/api'

interface UploadedAttachment {
  id: string
  url: string
  type: 'image' | 'video' | 'file'
  name: string
  size: number
}

interface WhisperComposerProps {
  onSendWhisper: (body: string, attachments?: UploadedAttachment[]) => Promise<void>
  disabled?: boolean
}

const MAX_FILE_SIZE = 10 * 1024 * 1024
const ALLOWED = '.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mov,.webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip'

export const WhisperComposer: React.FC<WhisperComposerProps> = ({ onSendWhisper, disabled }) => {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '36px'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px'
    }
  }, [message])

  const canSend = (message.trim() || attachments.length > 0) && !sending && !disabled && !uploading

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) return
    setError(null)
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file.size > MAX_FILE_SIZE) { setError(`"${file.name}" exceeds 10MB.`); continue }
      setUploading(true)
      try {
        const r = await apiUploadFile<UploadedAttachment>('/upload', file)
        setAttachments((prev) => [...prev, r])
      } catch (err) { setError(getErrorMessage(err, 'Upload failed')) }
      finally { setUploading(false) }
    }
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleSend = async () => {
    if (!canSend) return
    try {
      setSending(true)
      await onSendWhisper(message.trim(), attachments.length ? attachments : undefined)
      setMessage('')
      setAttachments([])
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="bg-amber-50 border-t border-dashed border-amber-300 dark:bg-amber-900/20 dark:border-amber-700">
      {/* attachment chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pt-2">
          {attachments.map((att) => (
            <div key={att.id} className="flex items-center gap-2 px-2 py-1 rounded-md bg-white dark:bg-surface-secondary border border-amber-300 text-xs">
              {att.type === 'image'
                ? <img src={att.url} alt="" className="w-6 h-6 rounded object-cover" />
                : <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25" /></svg>}
              <span className="max-w-[120px] truncate text-amber-900 dark:text-amber-200">{att.name}</span>
              <button onClick={() => setAttachments((p) => p.filter((a) => a.id !== att.id))} className="text-amber-400 hover:text-red-500">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="px-4 pt-1 text-[11px] text-red-600">{error}</p>}

      <div className="flex items-end gap-2 px-4 py-3">
        <div className="flex-shrink-0 pb-1">
          <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
        </div>

        {/* attach */}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={disabled || uploading}
          title="Attach media or document"
          className="flex-shrink-0 mb-0.5 p-1.5 rounded-md text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-800/40 disabled:opacity-40 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>
        </button>
        <input ref={fileRef} type="file" multiple accept={ALLOWED} className="hidden" onChange={handleFiles} />

        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Whisper to the agent (only visible to agents)…"
          disabled={disabled || sending}
          rows={1}
          style={{ minHeight: '36px', maxHeight: '120px', caretColor: 'currentColor' }}
          className="flex-1 resize-none rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-txt-primary caret-current placeholder:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent dark:bg-surface-secondary dark:border-amber-600 dark:placeholder:text-amber-600"
        />

        <button
          onClick={handleSend}
          disabled={!canSend}
          className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Send whisper (Enter)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
        </button>
      </div>
    </div>
  )
}
